import { useCallback, useMemo, useState } from 'react';
import { Anchor, Badge, Group, Text, Tooltip } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { DateCell, MoneyCell, TextCell } from '@ayocore/platform/client/components/RTable/cells';
import { UserCell } from '@ayocore/platform/identity/client/cells';
import { useDrawer } from '@ayocore/platform/client/hooks/useDrawer';
import { useDealRepository } from '@ayocore/erp/crm/client/rpc';
import type { Deal } from '@ayocore/erp/crm/schemas/deal.schema';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import { DealView } from '../../../Deals/views/DealView';

/**
 * Вкладка «Сделки» в LeadView — список сделок партнёра, к которому привязан лид.
 *
 * Использует общий RepositoryTable, отфильтрованный по `partner_id` лида.
 * Открытие сделки — через Drawer, как в DealsList. Идентификатор таблицы
 * привязан к leadId, чтобы пагинация/сортировка/фильтры в URL не конфликтовали
 * между разными карточками лидов.
 */
type DealExtended = Deal & {
  deal_status?: { id: number; name?: string; color?: string | null } | null;
  deal_owner?: { id: number; name?: string; email?: string } | null;
};

interface LeadPartnerDealsTabProps {
  leadId: number;
  partnerId: number;
}

export function LeadPartnerDealsTab({ leadId, partnerId }: LeadPartnerDealsTabProps) {
  const dealRepo = useDealRepository();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const dealDrawer = useDrawer(DealView);
  dealDrawer.onClose(() => setRefreshTrigger(prev => prev + 1));

  // Стабилизируем репозиторий — иначе RepositoryTable на каждом рендере шлёт refresh().
  const repository = useMemo(
    () => dealRepo.filterByPartner(partnerId).withRelations().limit(50),
    [dealRepo, partnerId]
  );

  const columns = useMemo(
    (): ColumnDef<DealExtended>[] => [
      {
        id: 'created_at',
        header: 'Дата',
        accessorKey: 'created_at',
        enableSorting: true,
        size: 120,
        cell: ({ row }) => <DateCell value={row.original.created_at} />,
      },
      {
        id: 'number',
        header: 'Сделка',
        accessorKey: 'number',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => (
          <Group gap={4} wrap='nowrap' align='center'>
            <Anchor
              onClick={e => {
                e.stopPropagation();
                dealDrawer.open({
                  dealId: row.original.id,
                  isModal: true,
                  showEventList: false,
                });
              }}
            >
              {row.original.number || `#${row.original.id}`}
            </Anchor>
            {row.original.archived && (
              <Tooltip label='Архивная' withArrow>
                <IconLock size={13} color='var(--mantine-color-dimmed)' />
              </Tooltip>
            )}
          </Group>
        ),
      },
      {
        id: 'deal_status.name',
        header: 'Статус',
        accessorFn: row => row.deal_status?.name ?? '',
        enableSorting: true,
        size: 150,
        cell: ({ row }) => {
          const status = row.original.deal_status;
          if (!status?.name)
            return (
              <Text size='xs' c='dimmed'>
                —
              </Text>
            );
          return (
            <Badge
              color={status.color || 'gray'}
              variant='light'
              size='sm'
              style={{
                backgroundColor: status.color ? `${status.color}20` : undefined,
                textTransform: 'none',
                fontWeight: 'normal',
              }}
            >
              {status.name}
            </Badge>
          );
        },
      },
      {
        id: 'amount',
        header: 'Сумма',
        accessorKey: 'amount',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => {
          const amount = row.original.amount;
          if (amount == null) return <TextCell value={null} c='dimmed' fs='italic' />;
          const currencyLabel =
            row.original.currency?.symbol || row.original.currency?.code || '₽';
          return <MoneyCell value={amount} suffix={` ${currencyLabel}`} />;
        },
      },
      {
        id: 'deal_owner.name',
        header: 'Ответственный',
        accessorFn: row => row.deal_owner?.name ?? '',
        enableSorting: true,
        size: 160,
        cell: ({ row }) => <UserCell user={row.original.deal_owner} fallback='Не назначен' />,
      },
    ],
    [dealDrawer]
  );

  const handleRowDoubleClick = useCallback(
    (_row: { original: DealExtended }, rowData: DealExtended) => {
      dealDrawer.open({ dealId: rowData.id, isModal: true, showEventList: false });
    },
    [dealDrawer]
  );

  return (
    <RepositoryTable<DealExtended>
      id={`crm-lead-${leadId}-deals`}
      repository={repository as unknown as RpcRepositoryClient<DealExtended>}
      columns={columns}
      rowHeight={1.2}
      onRowDoubleClick={handleRowDoubleClick}
      refreshTrigger={refreshTrigger}
    />
  );
}
